// ============================================================
// searchMatch.js
// How a typed search is compared with a stored value — forgivingly, and in one
// place.
//
// ONE COPY. This normaliser used to exist twice, byte for byte, in
// ShopDrawings.js and Dashboard.js, and the Documents page was about to become
// the third. Every copy is a place for the rule to quietly change in one screen
// and not the others — the same way the round-up table and the sidebar width
// went wrong.
//
// THE RULE, for every search box that uses it:
//   - case-insensitive
//   - punctuation, spaces, dashes and slashes ignored on BOTH sides: the stored
//     value and the query are normalised the same way before comparing
//   - matches anywhere in the value, not only at the start
//   - an empty or whitespace-only query is not a search
//
// SHORT QUERIES STAY STRICT. Below MIN_LOOSE_QUERY letters-or-digits, callers
// keep their previous exact behaviour. Normalised, a two-character query like
// "a1" is a substring of a large share of every document number and P.O. in the
// database; loosening it would turn a search into a dump.
// ============================================================

/** Lowercase, and strip everything that is not a letter or a digit. */
export const normalizeSearch = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Below this many letters and digits, a query is matched strictly. */
export const MIN_LOOSE_QUERY = 3;

/** Is this query long enough for forgiving matching? */
export const isLooseQuery = (query) => normalizeSearch(query).length >= MIN_LOOSE_QUERY;

/**
 * Forgiving substring match on normalised text.
 *
 * "PO-4471-A" normalises to "po4471a", so 4471, po4471, po 4471, PO-4471,
 * 4471-a and 4471a all find it.
 */
export function looseIncludes(value, query) {
  const nq = normalizeSearch(query);
  if (!nq) return false;
  return normalizeSearch(value).includes(nq);
}

// A name reduced to lowercase words separated by single spaces. Apostrophes and
// full stops are REMOVED, because they sit inside a word ("O'Brien", "A.B.C."),
// while every other run of punctuation or whitespace becomes ONE space, because
// it sits between words ("Four-Leaf", "Smith & Sons", "Bay  Ridge").
const spacedName = (v) => String(v ?? '')
  .toLowerCase()
  .replace(/['’.]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

/**
 * Forgiving match for PEOPLE AND BUSINESS NAMES.
 *
 * Names cannot simply have every space stripped. Squashed into one string,
 * "Wolf Lake Farms" becomes "wolflakefarms", and "flake" would match it — a
 * query that starts in the middle of one word and runs into the next, which no
 * person typing "flake" meant. So a name matches in one of two ways:
 *
 *   1. SPACED. Punctuation mapped to spaces on both sides, then an ordinary
 *      substring test. "four-leaf" finds "Four Leaf LLC"; "idge" still finds
 *      "Bay Ridge", exactly as the plain substring search always did.
 *
 *   2. SQUASHED, BUT ONLY FROM THE START OF A WORD. Spaces removed from both,
 *      and the query must begin where a word in the name begins. "fourleaf",
 *      "leafllc" and "obrien" all match; "flake" does not, because it would
 *      have to start inside "wolf".
 */
export function nameMatches(name, query) {
  const q = spacedName(query);
  const n = spacedName(name);
  if (!q || !n) return false;

  if (n.includes(q)) return true;

  const compactQuery = q.replace(/ /g, '');
  let compact = '';
  const wordStarts = [];
  n.split(' ').forEach(word => { wordStarts.push(compact.length); compact += word; });
  return wordStarts.some(start => compact.startsWith(compactQuery, start));
}

/**
 * Rebuild whatever was typed into the exact quote or invoice numbers it could
 * mean. Document numbers are "YY-NNNNNN" — 26-006389 — and people type them
 * several ways:
 *
 *   26-006389   the whole thing
 *   006389      the six-digit sequence
 *   6389        the sequence without its leading zeros
 *   26 6389     year and sequence, any separator
 *   266389      year and short sequence run together
 *
 * CANONICALISED RATHER THAN SEARCHED. Firestore has no substring operator, and
 * the part people leave off is at the front, so a prefix range cannot help. The
 * format is fixed, though, so each form can be turned back into the exact
 * string to look up — an equality query needing no index.
 *
 * The year is the only guess. When the input does not carry one, this year and
 * last year are both tried. When a bare run of 3 to 6 digits STARTS with this
 * or last year ("266389"), that reading is offered as well, alongside the plain
 * one ("26-266389"); both are real numbers someone could mean.
 */
export const canonicalDocNumbers = (raw, now = new Date()) => {
  const text = String(raw ?? '').trim();
  if (!text) return [];
  const digits = text.replace(/\D/g, '');
  if (!digits) return [];

  let year = null;
  let sequence = digits;
  const explicit = text.match(/^(\d{2})\s*[-–—/ ]\s*(\d{1,6})$/);
  if (explicit) {
    year = explicit[1];
    sequence = explicit[2];
  } else if (digits.length > 6) {
    // A bare 8-digit run: the last six are the sequence, the rest the year.
    year = digits.slice(0, digits.length - 6).slice(-2);
    sequence = digits.slice(-6);
  }
  if (sequence.length > 6) return [];

  const padded = sequence.padStart(6, '0');
  const thisYear = String(now.getFullYear()).slice(-2);
  const lastYear = String(now.getFullYear() - 1).slice(-2);
  const years = year ? [year] : [thisYear, lastYear];
  const out = new Set(years.map(y => `${y}-${padded}`));

  // "266389": year and a short sequence typed with nothing between them.
  if (!year && /^\d+$/.test(text) && digits.length >= 3 && digits.length <= 6) {
    const yy = digits.slice(0, 2);
    if (yy === thisYear || yy === lastYear) out.add(`${yy}-${digits.slice(2).padStart(6, '0')}`);
  }
  return [...out];
};

/**
 * Forgiving match for a stored quote or invoice number: a normalised substring
 * (6389, 006389, 26-006389) or one of the canonical rebuilds of the query
 * (26 6389, 266389), whose zero padding a plain substring test cannot see.
 */
export function documentNumberMatches(docNumber, query) {
  const dn = String(docNumber ?? '');
  if (!dn || !normalizeSearch(query)) return false;
  if (looseIncludes(dn, query)) return true;
  return canonicalDocNumbers(query).includes(dn);
}
